import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * AIProviderSettings - Page for configuring AI generation providers
 * for text, image, and video generation
 */
const AIProviderSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Provider configurations
  const [providers, setProviders] = useState({
    text: {
      provider: 'openai',
      api_key: '',
      model: 'gpt-4o-mini',
      enabled: true
    },
    image: {
      provider: 'openai',
      api_key: '',
      model: 'dall-e-3',
      enabled: true
    },
    video: {
      provider: 'stability',
      api_key: '',
      model: 'stable-video-diffusion',
      enabled: false
    }
  });
  
  // Usage statistics
  const [usage, setUsage] = useState({
    text: { requests: 0, tokens: 0, cost: 0 },
    image: { requests: 0, images: 0, cost: 0 },
    video: { requests: 0, videos: 0, cost: 0 }
  });
  
  // Active tab
  const [activeTab, setActiveTab] = useState('text');

  // Provider options
  const providerOptions = {
    text: [
      { value: 'openai', label: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo'] },
      { value: 'anthropic', label: 'Anthropic Claude', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
      { value: 'google', label: 'Google Gemini', models: ['gemini-pro', 'gemini-pro-vision'] }
    ],
    image: [
      { value: 'openai', label: 'OpenAI DALL-E', models: ['dall-e-3', 'dall-e-2'] },
      { value: 'stability', label: 'Stability AI', models: ['stable-diffusion-xl', 'stable-diffusion-3', 'stable-diffusion-2.1'] },
      { value: 'midjourney', label: 'Midjourney (API)', models: ['midjourney-v6', 'midjourney-v5.2'] },
      { value: 'replicate', label: 'Replicate', models: ['sdxl', 'kandinsky', 'pixart'] }
    ],
    video: [
      { value: 'stability', label: 'Stability AI', models: ['stable-video-diffusion', 'stable-video-diffusion-xt'] },
      { value: 'runway', label: 'Runway ML', models: ['gen-2', 'gen-1'] },
      { value: 'pika', label: 'Pika Labs', models: ['pika-1.0'] },
      { value: 'replicate', label: 'Replicate', models: ['zeroscope', 'modelscope'] }
    ]
  };

  // Cost estimates per provider
  const costEstimates = {
    text: {
      openai: { 'gpt-4o': '$0.005/1K tokens', 'gpt-4o-mini': '$0.00015/1K tokens', 'gpt-4-turbo': '$0.01/1K tokens', 'gpt-3.5-turbo': '$0.0015/1K tokens' },
      anthropic: { 'claude-3-opus': '$0.015/1K tokens', 'claude-3-sonnet': '$0.003/1K tokens', 'claude-3-haiku': '$0.00025/1K tokens' },
      google: { 'gemini-pro': '$0.00025/1K tokens', 'gemini-pro-vision': '$0.0025/1K tokens' }
    },
    image: {
      openai: { 'dall-e-3': '$0.04-0.12/image', 'dall-e-2': '$0.016-0.02/image' },
      stability: { 'stable-diffusion-xl': '$0.002-0.006/image', 'stable-diffusion-3': '$0.035-0.065/image', 'stable-diffusion-2.1': '$0.002/image' },
      midjourney: { 'midjourney-v6': '$0.01-0.02/image', 'midjourney-v5.2': '$0.01/image' },
      replicate: { 'sdxl': '$0.0023/image', 'kandinsky': '$0.0046/image', 'pixart': '$0.002/image' }
    },
    video: {
      stability: { 'stable-video-diffusion': '$0.05-0.15/video', 'stable-video-diffusion-xt': '$0.10-0.20/video' },
      runway: { 'gen-2': '$0.05/second', 'gen-1': '$0.025/second' },
      pika: { 'pika-1.0': '$0.10/video' },
      replicate: { 'zeroscope': '$0.02/video', 'modelscope': '$0.01/video' }
    }
  };

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Try to fetch existing settings
        try {
          const res = await api.get('/settings/ai-providers');
          if (res.data.providers) {
            setProviders(res.data.providers);
          }
          if (res.data.usage) {
            setUsage(res.data.usage);
          }
        } catch (err) {
          // Settings might not exist yet
        }
        
      } catch (err) {
        console.error('Error fetching settings:', err);
        setError('Failed to load provider settings');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  const handleProviderChange = useCallback((type, field, value) => {
    setProviders(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
        // Reset model when provider changes
        ...(field === 'provider' ? { model: providerOptions[type].find(p => p.value === value)?.models[0] || '' } : {})
      }
    }));
  }, []);

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      
      await api.put('/settings/ai-providers', { providers });
      
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type) => {
    try {
      setTesting(prev => ({ ...prev, [type]: true }));
      setError(null);
      
      const res = await api.post('/settings/ai-providers/test', {
        type,
        provider: providers[type].provider,
        api_key: providers[type].api_key,
        model: providers[type].model
      });
      
      if (res.data.success) {
        setSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} provider connection successful!`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.error || 'Connection test failed');
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setError(err.response?.data?.error || 'Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [type]: false }));
    }
  };

  const getProviderModels = (type, provider) => {
    const providerConfig = providerOptions[type].find(p => p.value === provider);
    return providerConfig?.models || [];
  };

  const getCostEstimate = (type, provider, model) => {
    return costEstimates[type]?.[provider]?.[model] || 'Contact provider';
  };

  // Styles
  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '1360px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 0.5rem 0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    subtitle: {
      color: '#6b7280',
      margin: 0
    },
    tabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
      paddingBottom: '0.5rem'
    },
    tab: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      borderRadius: '8px 8px 0 0',
      fontWeight: '500',
      color: '#6b7280',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s'
    },
    activeTab: {
      backgroundColor: '#7C3AED',
      color: 'white'
    },
    content: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '2rem'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    },
    cardHeader: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    cardTitle: {
      margin: 0,
      fontSize: '1rem',
      fontWeight: '600',
      color: '#374151'
    },
    cardBody: {
      padding: '1.5rem'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.5rem'
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.875rem',
      backgroundColor: 'white'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.875rem',
      fontFamily: 'monospace'
    },
    inputHint: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      cursor: 'pointer'
    },
    toggleSwitch: {
      width: '48px',
      height: '24px',
      backgroundColor: '#d1d5db',
      borderRadius: '12px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    toggleSwitchActive: {
      backgroundColor: '#7C3AED'
    },
    toggleKnob: {
      width: '20px',
      height: '20px',
      backgroundColor: 'white',
      borderRadius: '50%',
      position: 'absolute',
      top: '2px',
      left: '2px',
      transition: 'left 0.2s',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
    },
    toggleKnobActive: {
      left: '26px'
    },
    button: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: '#7C3AED',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    },
    successButton: {
      backgroundColor: '#10b981',
      color: 'white'
    },
    costBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '20px',
      fontSize: '0.75rem',
      color: '#374151',
      fontWeight: '500'
    },
    statCard: {
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      padding: '1rem',
      textAlign: 'center'
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: '#7C3AED'
    },
    statLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    alert: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    errorAlert: {
      backgroundColor: '#fef2f2',
      color: '#991b1b',
      border: '1px solid #fecaca'
    },
    successAlert: {
      backgroundColor: '#f0fdf4',
      color: '#166534',
      border: '1px solid #bbf7d0'
    },
    providerLogo: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.25rem'
    },
    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0',
      borderBottom: '1px solid #f3f4f6',
      fontSize: '0.875rem'
    }
  };

  const getTabIcon = (type) => {
    switch (type) {
      case 'text': return '✍️';
      case 'image': return '🖼️';
      case 'video': return '🎬';
      default: return '⚙️';
    }
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'openai': return '🤖';
      case 'anthropic': return '🧠';
      case 'google': return '🔮';
      case 'stability': return '🎨';
      case 'midjourney': return '🌟';
      case 'runway': return '🎥';
      case 'pika': return '⚡';
      case 'replicate': return '🔄';
      default: return '🔧';
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading provider settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <span>🤖</span> AI Provider Settings
        </h1>
        <p style={styles.subtitle}>
          Configure your AI generation providers for text, image, and video content
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ ...styles.alert, ...styles.errorAlert }}>
          <span>⚠️</span> {error}
        </div>
      )}
      {success && (
        <div style={{ ...styles.alert, ...styles.successAlert }}>
          <span>✓</span> {success}
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        {['text', 'image', 'video'].map(type => (
          <button
            key={type}
            style={{
              ...styles.tab,
              ...(activeTab === type ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab(type)}
          >
            <span>{getTabIcon(type)}</span>
            <span>{type.charAt(0).toUpperCase() + type.slice(1)} Generation</span>
            {providers[type].enabled && (
              <span style={{
                width: '8px',
                height: '8px',
                backgroundColor: activeTab === type ? 'white' : '#10b981',
                borderRadius: '50%'
              }} />
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {/* Configuration Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              {getTabIcon(activeTab)} {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Provider Configuration
            </h3>
            <div
              style={{
                ...styles.toggleSwitch,
                ...(providers[activeTab].enabled ? styles.toggleSwitchActive : {})
              }}
              onClick={() => handleProviderChange(activeTab, 'enabled', !providers[activeTab].enabled)}
            >
              <div style={{
                ...styles.toggleKnob,
                ...(providers[activeTab].enabled ? styles.toggleKnobActive : {})
              }} />
            </div>
          </div>
          
          <div style={styles.cardBody}>
            {/* Provider Selection */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Provider</label>
              <select
                value={providers[activeTab].provider}
                onChange={(e) => handleProviderChange(activeTab, 'provider', e.target.value)}
                style={styles.select}
                disabled={!providers[activeTab].enabled}
              >
                {providerOptions[activeTab].map(provider => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Model Selection */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Model</label>
              <select
                value={providers[activeTab].model}
                onChange={(e) => handleProviderChange(activeTab, 'model', e.target.value)}
                style={styles.select}
                disabled={!providers[activeTab].enabled}
              >
                {getProviderModels(activeTab, providers[activeTab].provider).map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
              <div style={{ marginTop: '0.5rem' }}>
                <span style={styles.costBadge}>
                  💰 {getCostEstimate(activeTab, providers[activeTab].provider, providers[activeTab].model)}
                </span>
              </div>
            </div>

            {/* API Key */}
            <div style={styles.formGroup}>
              <label style={styles.label}>API Key</label>
              <input
                type="password"
                value={providers[activeTab].api_key}
                onChange={(e) => handleProviderChange(activeTab, 'api_key', e.target.value)}
                style={styles.input}
                placeholder="sk-..."
                disabled={!providers[activeTab].enabled}
              />
              <p style={styles.inputHint}>
                Your API key is encrypted and stored securely
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => testConnection(activeTab)}
                disabled={!providers[activeTab].enabled || !providers[activeTab].api_key || testing[activeTab]}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  opacity: (!providers[activeTab].enabled || !providers[activeTab].api_key) ? 0.5 : 1
                }}
              >
                {testing[activeTab] ? (
                  <>⏳ Testing...</>
                ) : (
                  <>🔌 Test Connection</>
                )}
              </button>
              
              <button
                onClick={saveSettings}
                disabled={saving}
                style={{ ...styles.button, ...styles.primaryButton, flex: 1 }}
              >
                {saving ? (
                  <>💾 Saving...</>
                ) : (
                  <>💾 Save Settings</>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Usage & Stats Card */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>📊 Usage Statistics</h3>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Last 30 days</span>
          </div>
          
          <div style={styles.cardBody}>
            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  {activeTab === 'text' ? usage.text.requests : 
                   activeTab === 'image' ? usage.image.requests : 
                   usage.video.requests}
                </div>
                <div style={styles.statLabel}>Requests</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  {activeTab === 'text' ? (usage.text.tokens / 1000).toFixed(1) + 'K' : 
                   activeTab === 'image' ? usage.image.images : 
                   usage.video.videos}
                </div>
                <div style={styles.statLabel}>
                  {activeTab === 'text' ? 'Tokens' : activeTab === 'image' ? 'Images' : 'Videos'}
                </div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>
                  ${activeTab === 'text' ? usage.text.cost.toFixed(2) : 
                    activeTab === 'image' ? usage.image.cost.toFixed(2) : 
                    usage.video.cost.toFixed(2)}
                </div>
                <div style={styles.statLabel}>Est. Cost</div>
              </div>
            </div>

            {/* Provider Features */}
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
              {getProviderIcon(providers[activeTab].provider)} {providerOptions[activeTab].find(p => p.value === providers[activeTab].provider)?.label} Features
            </h4>
            
            <ul style={styles.featureList}>
              {activeTab === 'text' && providers[activeTab].provider === 'openai' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> Large context window (128K tokens)</li>
                  <li style={styles.featureItem}><span>✓</span> Function calling support</li>
                  <li style={styles.featureItem}><span>✓</span> JSON mode output</li>
                  <li style={styles.featureItem}><span>✓</span> Vision capabilities</li>
                </>
              )}
              {activeTab === 'text' && providers[activeTab].provider === 'anthropic' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> 200K context window</li>
                  <li style={styles.featureItem}><span>✓</span> Constitutional AI</li>
                  <li style={styles.featureItem}><span>✓</span> Long document analysis</li>
                  <li style={styles.featureItem}><span>✓</span> Code generation</li>
                </>
              )}
              {activeTab === 'image' && providers[activeTab].provider === 'openai' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> High quality outputs</li>
                  <li style={styles.featureItem}><span>✓</span> Natural language prompts</li>
                  <li style={styles.featureItem}><span>✓</span> Multiple sizes (1024x1024, 1792x1024)</li>
                  <li style={styles.featureItem}><span>✓</span> HD quality option</li>
                </>
              )}
              {activeTab === 'image' && providers[activeTab].provider === 'stability' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> Fine-grained control</li>
                  <li style={styles.featureItem}><span>✓</span> Negative prompts</li>
                  <li style={styles.featureItem}><span>✓</span> Multiple aspect ratios</li>
                  <li style={styles.featureItem}><span>✓</span> Image-to-image</li>
                  <li style={styles.featureItem}><span>✓</span> Inpainting & outpainting</li>
                </>
              )}
              {activeTab === 'video' && providers[activeTab].provider === 'stability' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> Image-to-video generation</li>
                  <li style={styles.featureItem}><span>✓</span> Motion control</li>
                  <li style={styles.featureItem}><span>✓</span> Camera movement</li>
                  <li style={styles.featureItem}><span>✓</span> 4-25 frames output</li>
                </>
              )}
              {activeTab === 'video' && providers[activeTab].provider === 'runway' && (
                <>
                  <li style={styles.featureItem}><span>✓</span> Text-to-video</li>
                  <li style={styles.featureItem}><span>✓</span> Image-to-video</li>
                  <li style={styles.featureItem}><span>✓</span> Video-to-video</li>
                  <li style={styles.featureItem}><span>✓</span> Motion brush</li>
                  <li style={styles.featureItem}><span>✓</span> Camera controls</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Settings */}
      <div style={{ marginTop: '2rem' }}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>⚙️ Advanced Settings</h3>
          </div>
          <div style={styles.cardBody}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
              {/* Rate Limiting */}
              <div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  🚦 Rate Limiting
                </h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Max requests/minute</label>
                  <input
                    type="number"
                    defaultValue={60}
                    style={styles.input}
                    min={1}
                    max={1000}
                  />
                </div>
              </div>

              {/* Fallback */}
              <div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  🔄 Fallback Provider
                </h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>If primary fails, use</label>
                  <select style={styles.select} defaultValue="">
                    <option value="">None (show error)</option>
                    <option value="queue">Queue for retry</option>
                    {providerOptions[activeTab].filter(p => p.value !== providers[activeTab].provider).map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Budget */}
              <div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>
                  💳 Budget Control
                </h4>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Monthly budget limit ($)</label>
                  <input
                    type="number"
                    defaultValue={100}
                    style={styles.input}
                    min={0}
                    step={10}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save All Button */}
      <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
        <button
          style={{ ...styles.button, ...styles.secondaryButton }}
        >
          Reset to Defaults
        </button>
        <button
          onClick={saveSettings}
          disabled={saving}
          style={{ ...styles.button, ...styles.primaryButton, padding: '1rem 2rem' }}
        >
          {saving ? (
            <>💾 Saving All Settings...</>
          ) : (
            <>💾 Save All Settings</>
          )}
        </button>
      </div>
    </div>
  );
};

export default AIProviderSettings;
